'use client';

import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { mapPropertyMarketingTypeToLeadContext } from '@pkgs/components/utils/tracking';
import { isUserAccountExclusiveListing } from '@pkgs/api/utils/searchResults';
import { type InputContact } from '@generated/search-api';
import {
  FlowEnum,
  getAgentContext,
  getLeadContext,
  getLocationContext,
  getNavigationContext,
  getPageContext,
  getPropertyContext,
  getShopContext,
  getUserContext,
  LeadIntentions,
  type SelfDescribingJson,
  trackLeadEvent,
  trackNavigationEvent,
  trackUserInteractionEvent,
  UserActionEnum,
} from '@ev-tech/snowplow-library';

import {
  getCookiesConsentContext,
  getShopContextCommonValues,
  mapFormDataToUserContext,
  mapListingToPropertyContext,
  mapPropertyToLocationContext,
} from '@/utils/snowplowContext';
import { getShopFromQueryCache } from '@/utils/getShopFromQueryCache';
import { useSettings } from '@/hooks/useSettings';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';

import { ExposeTrackingEffects } from './ExposeTrackingEffects';

export type ExposeTrackingContextType = {
  trackRequestExposeEvents: ({ elementId, navigationTarget }: { elementId: string; navigationTarget: 'source' | 'target' }) => void;
  trackExposeUserInteractionEvent: ({ elementId, action }: { elementId: string; action?: UserActionEnum }) => void;
  trackExposeNavigationEvent: ({
    targetUrl,
    elementId,
    shopContext,
    selectedOption,
  }: {
    targetUrl?: string;
    elementId: string;
    shopContext?: SelfDescribingJson;
    selectedOption?: string;
  }) => void;
  trackFormStatusDisplayNavigationEvent: (navigationContext: SelfDescribingJson) => void;
  trackExposeLeadEvent: ({ requestData, source }: { requestData: InputContact; source: string }) => void;
};

const initialStore: ExposeTrackingContextType = {
  trackRequestExposeEvents: () => undefined,
  trackExposeUserInteractionEvent: () => undefined,
  trackExposeNavigationEvent: () => undefined,
  trackFormStatusDisplayNavigationEvent: () => undefined,
  trackExposeLeadEvent: () => undefined,
};

export const ExposeTrackingContext = React.createContext<ExposeTrackingContextType>(initialStore);

export const ExposeTrackingProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    data: { listing, genericListing },
  } = useListing();
  const {
    pageTrackingTypes: { pageType, templateType },
    promotedBy,
  } = useExposePageProps();
  const { countryCode, language, measurementSystem } = useSettings();
  const queryClient = useQueryClient();

  const shop = getShopFromQueryCache({ queryClient, listing });

  const agentId = useMemo(() => promotedBy ?? genericListing.agent?.id, [promotedBy, genericListing.agent?.id]);

  const contexts = useMemo(
    () => ({
      pageContext: getPageContext({
        isHQPage: true,
        template_type: templateType,
        type: pageType,
        country_code: countryCode,
        page_language: language,
        isUaExclusive: isUserAccountExclusiveListing(genericListing),
      }),
      shopContext: listing ? [getShopContext(getShopContextCommonValues({ listing, shop }))] : [],
      agentContext: agentId
        ? [
            getAgentContext({
              agent_id: agentId,
              is_private_office: listing?.isPrivateOffice,
              is_private_office_ambassador: listing?.agent?.privateOfficeAmbassador,
            }),
          ]
        : [],
      cookieConsentContext: getCookiesConsentContext(),
      propertyContext: [getPropertyContext(mapListingToPropertyContext(genericListing, measurementSystem))],
      locationContext: [getLocationContext(mapPropertyToLocationContext(genericListing))],
    }),
    [pageType, templateType, countryCode, language, measurementSystem, genericListing, listing, agentId, shop],
  );

  const trackRequestExposeEvents = useCallback(
    ({ elementId, navigationTarget }: { elementId: string; navigationTarget: 'source' | 'target' }) => {
      trackNavigationEvent({
        screenName: '',
        contexts: [
          contexts.pageContext,
          getNavigationContext({
            ...(navigationTarget === 'target' && {
              target_screen: 'request-expose-modal-form',
            }),
            ...(navigationTarget === 'source' && {
              source_screen: 'request-expose-modal-form',
            }),
            element_id: elementId,
          }),
          ...contexts.shopContext,
          contexts.cookieConsentContext,
        ],
      });
    },
    [contexts],
  );

  const trackExposeUserInteractionEvent = useCallback(
    ({ elementId, action }: { elementId: string; action?: UserActionEnum }) => {
      trackUserInteractionEvent({
        elementId,
        action: action ?? UserActionEnum.click,
        contexts: [contexts.pageContext, ...contexts.shopContext, ...contexts.propertyContext, contexts.cookieConsentContext],
      });
    },
    [contexts],
  );

  const trackExposeNavigationEvent = useCallback(
    ({
      targetUrl,
      elementId,
      shopContext,
      selectedOption,
    }: {
      targetUrl?: string;
      elementId: string;
      shopContext?: SelfDescribingJson;
      selectedOption?: string;
    }) => {
      trackNavigationEvent({
        screenName: '',
        contexts: [
          getNavigationContext({ target_url: targetUrl, element_id: elementId, selected_option: selectedOption }),
          contexts.pageContext,
          ...(shopContext ? [shopContext] : contexts.shopContext),
          ...contexts.propertyContext,
          contexts.cookieConsentContext,
        ],
      });
    },
    [contexts],
  );

  const trackFormStatusDisplayNavigationEvent = useCallback(
    (navigationContext: SelfDescribingJson) => {
      trackNavigationEvent({
        screenName: '',
        contexts: [contexts.pageContext, navigationContext, ...contexts.shopContext, contexts.cookieConsentContext],
      });
    },
    [contexts],
  );

  const trackExposeLeadEvent = useCallback(
    ({ requestData, source }: { requestData: InputContact; source: string }) => {
      trackLeadEvent({
        source,
        contexts: [
          contexts.pageContext,
          ...contexts.propertyContext,
          ...contexts.shopContext,
          ...contexts.locationContext,
          getLeadContext(mapPropertyMarketingTypeToLeadContext(genericListing?.propertyMarketingType)),
          getUserContext(mapFormDataToUserContext(requestData)),
        ],
      });
      if (requestData.marketingConsent?.financing) {
        trackLeadEvent({
          source: `${source}-finance`,
          contexts: [
            contexts.pageContext,
            ...contexts.shopContext,
            getLeadContext({
              flow: FlowEnum.other,
              intention: LeadIntentions.finance,
            }),
            getUserContext({ email: requestData.email }),
          ],
        });
      }
    },
    [listing, contexts],
  );

  const value = useMemo(
    () => ({
      trackRequestExposeEvents,
      trackExposeUserInteractionEvent,
      trackExposeNavigationEvent,
      trackFormStatusDisplayNavigationEvent,
      trackExposeLeadEvent,
    }),
    [
      trackRequestExposeEvents,
      trackExposeUserInteractionEvent,
      trackExposeNavigationEvent,
      trackFormStatusDisplayNavigationEvent,
      trackExposeLeadEvent,
    ],
  );

  return (
    <ExposeTrackingContext.Provider value={value}>
      <ExposeTrackingEffects contexts={contexts} />
      {children}
    </ExposeTrackingContext.Provider>
  );
};
